import React, {useMemo} from 'react';
import {Divider} from 'antd';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {t} from '../../../../../../services/i18n';
import {Colors} from '../../../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import {dbTimeFormat, getBoxTimeFormat} from '../../../../../../services/Helpers';
import {findSwapEvent} from '../ChooseTrackSwapDay/trackSwapHelpers';
import VStack from '../../../../../common/Layout/VStack';
import HStack from '../../../../../common/Layout/HStack';
import Box from '../../../../../common/Layout/Box';
import AppText from '../../../../../common/AppText';
import UserAvatar from '../../../../../common/UserAvatar';

const cardStyle = {
    backgroundColor: Colors.newDesign.white,
    border: `1px solid ${Colors.slotBorder}`,
    borderRadius: 4,
    overflow: 'hidden',
};

const normalizeDay = (day) => ({
    ...day,
    id: day.id ?? day.series_event_id,
});

const formatSwapDayTimeLabel = (day, boxLocale, timeFormat, isTrackSwap) => {
    if (!day) {
        return '';
    }
    const weekday = day.day_of_week != null
        ? moment().weekday(day.day_of_week).locale(boxLocale).format('dddd')
        : day.day_name;
    const start = moment(day.start, dbTimeFormat).locale(boxLocale).format(timeFormat);
    const end = moment(day.end, dbTimeFormat).locale(boxLocale).format(timeFormat);
    if (isTrackSwap) {
        const activityPrefix = day.series_name ? `${day.series_name} – ` : '';
        return `${activityPrefix}${weekday} ${start}–${end}`;
    }
    return `${weekday}: ${start} - ${end}`;
};

const formatDateRange = (startDateRaw, endDateRaw, boxLocale, {noEndKey = null} = {}) => {
    if (!startDateRaw) {
        return null;
    }
    const formatDate = (date) => moment(date).locale(boxLocale).format(boxLocale === 'he' ? 'D MMM, YYYY' : 'MMM D, YYYY');
    const startDate = formatDate(startDateRaw);
    if (!endDateRaw) {
        return noEndKey ? t(noEndKey, {startDate}) : startDate;
    }
    return `${startDate} - ${formatDate(endDateRaw)}`;
};

const SwapDaySummary = ({target, values}) => {
    const user = target?.user;
    const swapDayData = values?.swapDayData;
    const lang = useSelector(state => state.userSettings.lang);
    const isTrackSwap = !!swapDayData?.track;

    const boxLocale = useMemo(() => (lang?.language === 'Hebrew' ? 'he' : 'en'), [lang]);
    const timeFormat = useMemo(() => getBoxTimeFormat(), []);

    const seriesTrackRelations = swapDayData?.seriesTrackRelations ?? [];

    const fromDay = useMemo(() => {
        if (isTrackSwap) {
            const context = findSwapEvent(seriesTrackRelations, values.fromSeriesEventId);
            if (!context) {
                return null;
            }
            return {
                ...normalizeDay(context.day),
                series_name: context.seriesTrackRelation.series?.series_name,
            };
        }
        return swapDayData?.current_days?.map(normalizeDay).find(day => day.id === values.fromSeriesEventId);
    }, [isTrackSwap, seriesTrackRelations, swapDayData, values.fromSeriesEventId]);

    const toDay = useMemo(() => {
        if (isTrackSwap) {
            const context = findSwapEvent(seriesTrackRelations, values.toSeriesEventId);
            if (!context) {
                return null;
            }
            return {
                ...normalizeDay(context.day),
                series_name: context.seriesTrackRelation.series?.series_name,
            };
        }
        return swapDayData?.all_days?.find(day => day.id === values.toSeriesEventId) ?? null;
    }, [isTrackSwap, seriesTrackRelations, swapDayData, values.toSeriesEventId]);

    const dateRange = useMemo(() => {
        if (isTrackSwap) {
            return formatDateRange(swapDayData?.track?.start_date, swapDayData?.track?.end_date, boxLocale);
        }
        return formatDateRange(
            swapDayData?.start_date ?? swapDayData?.series_start_date,
            swapDayData?.end_date ?? swapDayData?.series_end_date,
            boxLocale,
            {noEndKey: 'no-end-date-hugim'},
        );
    }, [isTrackSwap, swapDayData, boxLocale]);

    const bookingTypeLabel = useMemo(() => {
        if (!isTrackSwap) {
            return null;
        }
        const sessionPerWeek = swapDayData?.track?.session_per_week;
        if (sessionPerWeek == null) {
            return t('unlimited');
        }
        return `${sessionPerWeek} ${t('weeklyFrequency')}`;
    }, [isTrackSwap, swapDayData]);

    const nameLabel = isTrackSwap ? t('track-swap-summary-track-label') : t('courseLimitationsCourseName');
    const datesLabel = isTrackSwap ? t('track-swap-summary-dates-label') : t('date-of-hug-label');
    const entityName = isTrackSwap ? (swapDayData?.track?.name ?? '') : (swapDayData?.series_name ?? '');

    const fromDayLabel = formatSwapDayTimeLabel(fromDay, boxLocale, timeFormat, isTrackSwap);
    const toDayLabel = formatSwapDayTimeLabel(toDay, boxLocale, timeFormat, isTrackSwap);

    return (
        <VStack space={'0'} w={'100%'}>
            <Box style={{height: 1, backgroundColor: Colors.slotBorder}}/>
            <VStack space={'0.75rem'} style={{paddingTop: '27px'}}>
                <AppText boldFont fontSize={GlobalStyleAttributes.fonts.heading2Desktop} color={Colors.newDesign.mainText}>
                    {t('bookingDetails')}
                </AppText>
                <VStack space={'0'} style={cardStyle}>
                    <Box style={{padding: '12px'}}>
                        <VStack space={'0.75rem'}>
                            {user && (
                                <HStack space={'0.75rem'} alignItems={'center'}>
                                    <UserAvatar
                                        imageUri={user.image}
                                        firstName={user.firstName}
                                        lastName={user.lastName}
                                        size={31}
                                    />
                                    <AppText mediumFont style={{lineHeight: '24px'}}>
                                        {user.firstName} {user.lastName}
                                    </AppText>
                                </HStack>
                            )}
                            <VStack space={'0.75rem'}>
                                <HStack space={'0.25rem'} alignItems={'center'} flexWrap={'wrap'}>
                                    <AppText mediumFont>{nameLabel}</AppText>
                                    <AppText>{entityName}</AppText>
                                </HStack>
                                {dateRange && (
                                    <HStack space={'0.25rem'} alignItems={'center'} flexWrap={'wrap'}>
                                        <AppText mediumFont>{datesLabel}</AppText>
                                        <AppText>{dateRange}</AppText>
                                    </HStack>
                                )}
                                {isTrackSwap && (
                                    <HStack space={'0.25rem'} alignItems={'center'} flexWrap={'wrap'}>
                                        <AppText mediumFont>{`${t('booking-type')}:`}</AppText>
                                        <AppText>{bookingTypeLabel}</AppText>
                                    </HStack>
                                )}
                            </VStack>
                        </VStack>
                    </Box>
                    <Divider style={{margin: 0, borderColor: Colors.slotBorder}}/>
                    <Box style={{padding: '12px'}}>
                        <VStack space={'0.75rem'}>
                            <VStack space={'0'}>
                                <AppText mediumFont>{`${t('swap-day-replace-label')}:`}</AppText>
                                <AppText>{fromDayLabel}</AppText>
                            </VStack>
                            <VStack space={'0'}>
                                <AppText mediumFont>{`${t('swap-day-to-label')}:`}</AppText>
                                <AppText>{toDayLabel}</AppText>
                            </VStack>
                        </VStack>
                    </Box>
                </VStack>
            </VStack>
        </VStack>
    );
};

export default SwapDaySummary;
